import { useRef, useState } from 'react';
import { Button, Checkbox, Form, FormField, Input, Modal, Select, Spinner, Textarea } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import {
  Agent,
  FesConfig,
  JiraProject,
  Team,
  UnclaimedInstallation,
  createTeam,
  createWebhookKey,
  deleteWebhookKey,
  getAgents,
  getTeam,
  getUnclaimedInstallations,
  getWebhookKeys,
  installTeam,
  updateTeam,
  WebhookKeyCreatedResponse,
} from '../../fes';
import { TeamFormState, TeamPanelMode, emptyTeamForm, isTeamStatus, teamToForm } from './teamForm';
import { TeamDetailPanel } from './TeamDetailPanel';
import { TeamsTable } from './TeamsTable';
import { WebhookKeysTable } from './WebhookKeysTable';

type CreateTeamFormState = {
  name: string;
  description: string;
  installationId: string;
  generateWebhookKey: boolean;
  projectKey: string;
  agentId: string;
  webhookKeyDescription: string;
};

type CreateWebhookKeyFormState = {
  projectKey: string;
  agentId: string;
  description: string;
};

const emptyCreateTeamForm: CreateTeamFormState = {
  name: '',
  description: '',
  installationId: '',
  generateWebhookKey: false,
  projectKey: '',
  agentId: '',
  webhookKeyDescription: '',
};

const emptyCreateWebhookKeyForm: CreateWebhookKeyFormState = {
  projectKey: '',
  agentId: '',
  description: '',
};

const jiraProjectKeyPattern = /^[A-Z][A-Z0-9_]{1,9}$/;

export function TeamsView({
  fesConfig,
  tokens,
  teams,
  onTeamSaved,
}: {
  fesConfig: FesConfig;
  tokens: AuthTokens;
  teams: Team[];
  onTeamSaved: (team: Team) => void;
}) {
  const latestTeamRequest = useRef(0);
  const [selectedTeamId, setSelectedTeamId] = useState<string>();
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [form, setForm] = useState<TeamFormState>(emptyTeamForm);
  const [mode, setMode] = useState<TeamPanelMode>('read');
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [panelError, setPanelError] = useState<string>();
  const [saveError, setSaveError] = useState<string>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTeamFormState>(emptyCreateTeamForm);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [createError, setCreateError] = useState<string>();
  const [unclaimedInstallations, setUnclaimedInstallations] = useState<UnclaimedInstallation[]>([]);
  const [areInstallationsLoading, setAreInstallationsLoading] = useState(false);
  const [installationsError, setInstallationsError] = useState<string>();
  const [createFlowTeam, setCreateFlowTeam] = useState<Team | null>(null);
  const [webhookKeys, setWebhookKeys] = useState<JiraProject[]>([]);
  const [areWebhookKeysLoading, setAreWebhookKeysLoading] = useState(false);
  const [webhookKeysError, setWebhookKeysError] = useState<string>();
  const [webhookKeyToRevoke, setWebhookKeyToRevoke] = useState<JiraProject | null>(null);
  const [isRevokingWebhookKey, setIsRevokingWebhookKey] = useState(false);
  const [revokeWebhookKeyError, setRevokeWebhookKeyError] = useState<string>();
  const [isGenerateWebhookKeyModalOpen, setIsGenerateWebhookKeyModalOpen] = useState(false);
  const [createWebhookKeyForm, setCreateWebhookKeyForm] =
    useState<CreateWebhookKeyFormState>(emptyCreateWebhookKeyForm);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [areAgentsLoading, setAreAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string>();
  const [isGeneratingWebhookKey, setIsGeneratingWebhookKey] = useState(false);
  const [generateWebhookKeyError, setGenerateWebhookKeyError] = useState<string>();
  const [generatedWebhookKey, setGeneratedWebhookKey] = useState<WebhookKeyCreatedResponse['data']>();
  const [copyWebhookKeyStatus, setCopyWebhookKeyStatus] = useState<string>();

  const handleSelect = (team: Team) => {
    if (!team.teamId) {
      return;
    }

    const requestId = latestTeamRequest.current + 1;
    latestTeamRequest.current = requestId;
    setSelectedTeamId(team.teamId);
    setSelectedTeam(team);
    setForm(teamToForm(team));
    setMode('read');
    setPanelError(undefined);
    setSaveError(undefined);
    setIsDetailLoading(true);
    setWebhookKeys([]);
    setWebhookKeysError(undefined);
    setAreWebhookKeysLoading(true);

    getWebhookKeys(fesConfig, tokens, team.teamId)
      .then(webhookKeysResponse => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        setWebhookKeys(webhookKeysResponse.data || []);
      })
      .catch(error => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        setWebhookKeysError(error instanceof Error ? error.message : 'Unable to load webhook keys.');
      })
      .finally(() => {
        if (latestTeamRequest.current === requestId) {
          setAreWebhookKeysLoading(false);
        }
      });

    getTeam(fesConfig, tokens, team.teamId)
      .then(teamResponse => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        const detailedTeam = teamResponse.data || team;
        setSelectedTeam(detailedTeam);
        setForm(teamToForm(detailedTeam));
      })
      .catch(error => {
        if (latestTeamRequest.current !== requestId) {
          return;
        }

        setPanelError(error instanceof Error ? error.message : 'Unable to load team details.');
      })
      .finally(() => {
        if (latestTeamRequest.current === requestId) {
          setIsDetailLoading(false);
        }
      });
  };

  const handleFormChange = (field: keyof TeamFormState, value: string) => {
    setForm(currentForm => {
      if (field === 'status') {
        return isTeamStatus(value) ? { ...currentForm, status: value } : currentForm;
      }

      return {
        ...currentForm,
        [field]: value,
      };
    });
  };

  const handleEdit = () => {
    setMode('edit');
    setPanelError(undefined);
    setSaveError(undefined);
  };

  const handleCancel = () => {
    if (selectedTeam) {
      setForm(teamToForm(selectedTeam));
    }

    setMode('read');
    setPanelError(undefined);
    setSaveError(undefined);
  };

  const handleSaveErrorClose = () => {
    setSaveError(undefined);
  };

  const handleCreateOpen = () => {
    setCreateForm({
      ...emptyCreateTeamForm,
      agentId: agents[0]?.agentId || '',
    });
    setCreateError(undefined);
    setInstallationsError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCreateFlowTeam(null);
    setIsCreateModalOpen(true);
    loadUnclaimedInstallationsForCreateForm();
  };

  const handleCreateClose = () => {
    if (isCreatingTeam) {
      return;
    }

    if (generatedWebhookKey && createFlowTeam) {
      handleCreateTeamResultClose();
      return;
    }

    setIsCreateModalOpen(false);
    setCreateForm(emptyCreateTeamForm);
    setCreateError(undefined);
    setInstallationsError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCreateFlowTeam(null);
  };

  const handleCreateFormChange = (field: keyof CreateTeamFormState, value: string | boolean) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      [field]: field === 'projectKey' && typeof value === 'string' ? value.toUpperCase() : value,
    }));
    setCreateError(undefined);
  };

  const handleCreateGenerateWebhookKeyChange = (checked: boolean) => {
    setCreateForm(currentForm => ({
      ...currentForm,
      generateWebhookKey: checked,
      agentId: checked ? currentForm.agentId || agents[0]?.agentId || '' : currentForm.agentId,
    }));
    setCreateError(undefined);

    if (checked) {
      loadAgentsForWebhookKeyForm('create');
    }
  };

  const handleCreateTeam = () => {
    const name = createForm.name.trim();

    if (!name) {
      setCreateError('Name is required.');
      return;
    }

    const selectedInstallationId = createForm.installationId.trim();
    const parsedInstallationId = Number(selectedInstallationId);

    if (
      selectedInstallationId &&
      (!/^\d+$/.test(selectedInstallationId) ||
        !Number.isSafeInteger(parsedInstallationId) ||
        parsedInstallationId <= 0)
    ) {
      setCreateError('Installation ID must be a positive integer.');
      return;
    }

    if (createForm.generateWebhookKey && !selectedInstallationId) {
      setCreateError('Installation ID is required to generate a webhook key.');
      return;
    }

    const projectKey = createForm.projectKey.trim().toUpperCase();

    if (createForm.generateWebhookKey && !jiraProjectKeyPattern.test(projectKey)) {
      setCreateError(
        'Project key must start with an uppercase letter and contain 2-10 uppercase letters, numbers, or underscores.'
      );
      return;
    }

    if (createForm.generateWebhookKey && !createForm.agentId) {
      setCreateError('Agent is required.');
      return;
    }

    setIsCreatingTeam(true);
    setCreateError(undefined);
    setCopyWebhookKeyStatus(undefined);

    const create = async () => {
      try {
        const description = createForm.description.trim();
        const webhookKeyDescription = createForm.webhookKeyDescription.trim();
        const teamResponse = await createTeam(fesConfig, tokens, {
          name,
          ...(description ? { description } : {}),
        });
        const createdTeam = teamResponse.data;

        if (!createdTeam?.teamId) {
          throw new Error('FES did not return a team ID for the created team.');
        }

        let savedTeam = createdTeam;

        if (selectedInstallationId) {
          const installResponse = await installTeam(fesConfig, tokens, createdTeam.teamId, {
            installationId: parsedInstallationId,
          });
          savedTeam = installResponse.data || {
            ...createdTeam,
            installationId: parsedInstallationId,
          };
        }

        if (createForm.generateWebhookKey) {
          const webhookKeyResponse = await createWebhookKey(fesConfig, tokens, savedTeam.teamId, {
            projectKey,
            agentId: createForm.agentId,
            ...(webhookKeyDescription ? { description: webhookKeyDescription } : {}),
          });

          if (!webhookKeyResponse.data?.token) {
            throw new Error('FES did not return a generated webhook key.');
          }

          setCreateFlowTeam(savedTeam);
          setGeneratedWebhookKey(webhookKeyResponse.data);
          return;
        }

        setIsCreateModalOpen(false);
        setCreateForm(emptyCreateTeamForm);
        setCreateFlowTeam(null);
        onTeamSaved(savedTeam);
        handleSelect(savedTeam);
      } catch (error) {
        setCreateError(error instanceof Error ? error.message : 'Unable to complete team onboarding.');
      } finally {
        setIsCreatingTeam(false);
      }
    };

    void create();
  };

  const handleCreateTeamResultClose = () => {
    const savedTeam = createFlowTeam;

    setIsCreateModalOpen(false);
    setCreateForm(emptyCreateTeamForm);
    setCreateError(undefined);
    setInstallationsError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
    setCreateFlowTeam(null);

    if (savedTeam) {
      onTeamSaved(savedTeam);
      handleSelect(savedTeam);
    }
  };

  const loadUnclaimedInstallationsForCreateForm = () => {
    setAreInstallationsLoading(true);
    setInstallationsError(undefined);

    const loadInstallations = async () => {
      try {
        const installationsResponse = await getUnclaimedInstallations(fesConfig, tokens);
        setUnclaimedInstallations(installationsResponse.data || []);
      } catch (error) {
        setInstallationsError(error instanceof Error ? error.message : 'Unable to load unclaimed installations.');
      } finally {
        setAreInstallationsLoading(false);
      }
    };

    void loadInstallations();
  };

  const refreshWebhookKeys = async (teamId: string, requestId = latestTeamRequest.current) => {
    setAreWebhookKeysLoading(true);
    setWebhookKeysError(undefined);

    try {
      const webhookKeysResponse = await getWebhookKeys(fesConfig, tokens, teamId);

      if (latestTeamRequest.current === requestId) {
        setWebhookKeys(webhookKeysResponse.data || []);
      }
    } catch (error) {
      if (latestTeamRequest.current === requestId) {
        setWebhookKeysError(error instanceof Error ? error.message : 'Unable to load webhook keys.');
      }
    } finally {
      if (latestTeamRequest.current === requestId) {
        setAreWebhookKeysLoading(false);
      }
    }
  };

  const loadAgentsForWebhookKeyForm = (target: 'standalone' | 'create' = 'standalone') => {
    if (agents.length > 0) {
      setAgentsError(undefined);
      if (target === 'create') {
        setCreateForm(currentForm => ({
          ...currentForm,
          agentId: currentForm.agentId || agents[0].agentId,
        }));
      } else {
        setCreateWebhookKeyForm(currentForm => ({
          ...currentForm,
          agentId: currentForm.agentId || agents[0].agentId,
        }));
      }
      return;
    }

    setAreAgentsLoading(true);
    setAgentsError(undefined);

    const loadAgents = async () => {
      try {
        const agentsResponse = await getAgents(fesConfig, tokens);
        const loadedAgents = agentsResponse.data || [];

        setAgents(loadedAgents);
        setAgentsError(loadedAgents.length === 0 ? 'No agents found.' : undefined);
        if (target === 'create') {
          setCreateForm(currentForm => ({
            ...currentForm,
            agentId: currentForm.agentId || loadedAgents[0]?.agentId || '',
          }));
        } else {
          setCreateWebhookKeyForm(currentForm => ({
            ...currentForm,
            agentId: currentForm.agentId || loadedAgents[0]?.agentId || '',
          }));
        }
      } catch (error) {
        setAgentsError(error instanceof Error ? error.message : 'Unable to load agents.');
      } finally {
        setAreAgentsLoading(false);
      }
    };

    void loadAgents();
  };

  const handleGenerateWebhookKeyOpen = () => {
    if (!(selectedTeamId || selectedTeam?.teamId)) {
      return;
    }

    setCreateWebhookKeyForm({
      ...emptyCreateWebhookKeyForm,
      agentId: agents[0]?.agentId || '',
    });
    setGenerateWebhookKeyError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
    setIsGenerateWebhookKeyModalOpen(true);
    loadAgentsForWebhookKeyForm();
  };

  const handleGenerateWebhookKeyClose = () => {
    if (isGeneratingWebhookKey) {
      return;
    }

    setIsGenerateWebhookKeyModalOpen(false);
    setCreateWebhookKeyForm(emptyCreateWebhookKeyForm);
    setGenerateWebhookKeyError(undefined);
    setGeneratedWebhookKey(undefined);
    setCopyWebhookKeyStatus(undefined);
  };

  const handleGenerateWebhookKeyResultClose = () => {
    const teamId = selectedTeamId || selectedTeam?.teamId;
    const requestId = latestTeamRequest.current;

    handleGenerateWebhookKeyClose();

    if (teamId) {
      void refreshWebhookKeys(teamId, requestId);
    }
  };

  const handleCreateWebhookKeyFormChange = (field: keyof CreateWebhookKeyFormState, value: string) => {
    setCreateWebhookKeyForm(currentForm => ({
      ...currentForm,
      [field]: field === 'projectKey' ? value.toUpperCase() : value,
    }));
    setGenerateWebhookKeyError(undefined);
  };

  const handleCreateWebhookKey = () => {
    const teamId = selectedTeamId || selectedTeam?.teamId;

    if (!teamId) {
      return;
    }

    const projectKey = createWebhookKeyForm.projectKey.trim().toUpperCase();

    if (!jiraProjectKeyPattern.test(projectKey)) {
      setGenerateWebhookKeyError(
        'Project key must start with an uppercase letter and contain 2-10 uppercase letters, numbers, or underscores.'
      );
      return;
    }

    if (!createWebhookKeyForm.agentId) {
      setGenerateWebhookKeyError('Agent is required.');
      return;
    }

    const description = createWebhookKeyForm.description.trim();

    setIsGeneratingWebhookKey(true);
    setGenerateWebhookKeyError(undefined);
    setCopyWebhookKeyStatus(undefined);

    const generate = async () => {
      try {
        const webhookKeyResponse = await createWebhookKey(fesConfig, tokens, teamId, {
          projectKey,
          agentId: createWebhookKeyForm.agentId,
          ...(description ? { description } : {}),
        });

        if (!webhookKeyResponse.data?.token) {
          throw new Error('FES did not return a generated webhook key.');
        }

        setGeneratedWebhookKey(webhookKeyResponse.data);
      } catch (error) {
        setGenerateWebhookKeyError(error instanceof Error ? error.message : 'Unable to generate webhook key.');
      } finally {
        setIsGeneratingWebhookKey(false);
      }
    };

    void generate();
  };

  const handleGenerateWebhookKeyConfirm = () => {
    if (generatedWebhookKey) {
      handleGenerateWebhookKeyResultClose();
      return;
    }

    handleCreateWebhookKey();
  };

  const handleCopyGeneratedWebhookKey = () => {
    if (!generatedWebhookKey?.token) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      setCopyWebhookKeyStatus('Clipboard API is not available.');
      return;
    }

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(generatedWebhookKey.token);
        setCopyWebhookKeyStatus('Copied.');
      } catch {
        setCopyWebhookKeyStatus('Unable to copy the key.');
      }
    };

    void copy();
  };

  const handleRevokeWebhookKeyOpen = (webhookKey: JiraProject) => {
    setWebhookKeyToRevoke(webhookKey);
    setRevokeWebhookKeyError(undefined);
  };

  const handleRevokeWebhookKeyClose = () => {
    if (isRevokingWebhookKey) {
      return;
    }

    setWebhookKeyToRevoke(null);
    setRevokeWebhookKeyError(undefined);
  };

  const handleRevokeWebhookKey = () => {
    if (!selectedTeamId || !webhookKeyToRevoke) {
      return;
    }

    const teamId = selectedTeamId;
    const projectKey = webhookKeyToRevoke.projectKey;
    const requestId = latestTeamRequest.current;

    setIsRevokingWebhookKey(true);
    setRevokeWebhookKeyError(undefined);

    const revoke = async () => {
      try {
        await deleteWebhookKey(fesConfig, tokens, teamId, projectKey);
      } catch (error) {
        setRevokeWebhookKeyError(error instanceof Error ? error.message : 'Unable to revoke webhook key.');
        setIsRevokingWebhookKey(false);
        return;
      }

      setWebhookKeyToRevoke(null);
      try {
        await refreshWebhookKeys(teamId, requestId);
      } finally {
        setIsRevokingWebhookKey(false);
      }
    };

    void revoke();
  };

  const handleSave = () => {
    if (!selectedTeamId) {
      return;
    }

    if (!form.name.trim()) {
      setPanelError('Name is required.');
      return;
    }

    const currentInstallationId = typeof selectedTeam?.installationId === 'number' ? String(selectedTeam.installationId) : '';
    const nextInstallationId = form.installationId.trim();
    const installationIdChanged = nextInstallationId !== currentInstallationId;
    const parsedInstallationId = Number(nextInstallationId);
    const hasValidInstallationId =
      nextInstallationId.length > 0 &&
      /^\d+$/.test(nextInstallationId) &&
      Number.isSafeInteger(parsedInstallationId) &&
      parsedInstallationId > 0;

    if (installationIdChanged && !hasValidInstallationId) {
      setPanelError('Installation ID must be a positive integer.');
      return;
    }

    setMode('saving');
    setPanelError(undefined);

    const save = async () => {
      try {
        const teamResponse = await updateTeam(fesConfig, tokens, selectedTeamId, {
          name: form.name.trim(),
          description: form.description,
          status: form.status,
        });
        let savedTeam: Team = teamResponse.data || {
          ...(selectedTeam || { teamId: selectedTeamId, name: form.name.trim(), status: form.status }),
          name: form.name.trim(),
          description: form.description,
          status: form.status,
        };

        if (installationIdChanged) {
          const installResponse = await installTeam(fesConfig, tokens, selectedTeamId, {
            installationId: parsedInstallationId,
          });
          savedTeam = installResponse.data || {
            ...savedTeam,
            installationId: parsedInstallationId,
          };
        }

        setSelectedTeam(savedTeam);
        setForm(teamToForm(savedTeam));
        setMode('read');
        onTeamSaved(savedTeam);
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Unable to save team.');
        setMode('edit');
      }
    };

    void save();
  };

  const canGenerateWebhookKey =
    !generatedWebhookKey &&
    !areAgentsLoading &&
    !isGeneratingWebhookKey &&
    jiraProjectKeyPattern.test(createWebhookKeyForm.projectKey.trim().toUpperCase()) &&
    Boolean(createWebhookKeyForm.agentId);
  const canSaveCreateTeam =
    !isCreatingTeam &&
    Boolean(createForm.name.trim()) &&
    (!createForm.generateWebhookKey ||
      (!areAgentsLoading &&
        Boolean(createForm.installationId) &&
        jiraProjectKeyPattern.test(createForm.projectKey.trim().toUpperCase()) &&
        Boolean(createForm.agentId)));

  return (
    <>
      <div className="app-table-toolbar">
        <Button onClick={handleCreateOpen}>Create team</Button>
      </div>
      <TeamsTable teams={teams} selectedTeamId={selectedTeamId} onSelectTeam={handleSelect} />
      <TeamDetailPanel
        error={panelError}
        form={form}
        isLoading={isDetailLoading}
        mode={mode}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onFormChange={handleFormChange}
        onSave={handleSave}
        team={selectedTeam}
      />
      {selectedTeam || isDetailLoading ? (
        <WebhookKeysTable
          error={webhookKeysError}
          isLoading={areWebhookKeysLoading}
          onGenerateWebhookKey={handleGenerateWebhookKeyOpen}
          onRevokeWebhookKey={handleRevokeWebhookKeyOpen}
          revokingProjectKey={isRevokingWebhookKey ? webhookKeyToRevoke?.projectKey : undefined}
          webhookKeys={webhookKeys}
        />
      ) : null}
      {saveError ? (
        <Modal
          buttonCloseLabel="Close"
          buttonConfirmText="OK"
          onClose={handleSaveErrorClose}
          onConfirm={handleSaveErrorClose}
          shown
          status="error"
          title="Unable to save team"
        >
          {saveError}
        </Modal>
      ) : null}
      {isGenerateWebhookKeyModalOpen ? (
        <Modal
          buttonCancelLabel="Cancel generate webhook key"
          buttonCancelText={generatedWebhookKey ? undefined : 'Cancel'}
          buttonCloseLabel="Close"
          buttonConfirmLabel={generatedWebhookKey ? 'Close generated webhook key' : 'Generate webhook key'}
          buttonConfirmText={generatedWebhookKey ? 'OK' : isGeneratingWebhookKey ? 'Generating...' : 'Generate'}
          confirmationDisabled={generatedWebhookKey ? false : !canGenerateWebhookKey}
          onCancel={generatedWebhookKey ? undefined : handleGenerateWebhookKeyClose}
          onClose={generatedWebhookKey ? handleGenerateWebhookKeyResultClose : handleGenerateWebhookKeyClose}
          onConfirm={handleGenerateWebhookKeyConfirm}
          shown
          status={generatedWebhookKey ? 'warning' : undefined}
          title={generatedWebhookKey ? 'Webhook key generated' : 'Generate webhook key'}
        >
          {generatedWebhookKey ? (
            <div className="app-webhook-key-result">
              <div className="app-webhook-key-warning" role="alert">
                This key will only be displayed this time and can't be regenerated. Copy it before closing this dialog.
              </div>
              <FormField labelForId="generated-webhook-key-token" labelText="Generated key" type="textarea">
                <Textarea id="generated-webhook-key-token" value={generatedWebhookKey.token} readOnly rows={5} />
              </FormField>
              <div className="app-copy-row">
                <Button secondary onClick={handleCopyGeneratedWebhookKey}>
                  Copy generated key
                </Button>
                {copyWebhookKeyStatus ? (
                  <span className="app-copy-status" role="status">
                    {copyWebhookKeyStatus}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {generateWebhookKeyError ? (
                <div className="app-result app-team-error" role="alert">
                  {generateWebhookKeyError}
                </div>
              ) : null}
              {agentsError ? (
                <div className="app-result app-team-error" role="alert">
                  {agentsError}
                </div>
              ) : null}
              {areAgentsLoading ? (
                <div className="app-detail-loading" role="status">
                  <Spinner center>Loading agents...</Spinner>
                </div>
              ) : null}
              <Form
                className="app-create-team-form"
                onSubmit={event => {
                  event.preventDefault();
                  handleCreateWebhookKey();
                }}
              >
                <FormField labelForId="create-webhook-key-project-key" labelText="Project Key" notion type="input">
                  <Input
                    id="create-webhook-key-project-key"
                    value={createWebhookKeyForm.projectKey}
                    onChange={event => handleCreateWebhookKeyFormChange('projectKey', event.target.value)}
                    readOnly={isGeneratingWebhookKey}
                  />
                </FormField>
                <FormField labelForId="create-webhook-key-agent" labelText="Agent" notion type="select">
                  <Select
                    id="create-webhook-key-agent"
                    value={createWebhookKeyForm.agentId}
                    onChange={event => handleCreateWebhookKeyFormChange('agentId', event.target.value)}
                    disabled={areAgentsLoading || isGeneratingWebhookKey}
                  >
                    <Select.Item value="" disabled>
                      Select an agent
                    </Select.Item>
                    {agents.map(agent => (
                      <Select.Item key={agent.agentId} value={agent.agentId}>
                        {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
                      </Select.Item>
                    ))}
                  </Select>
                </FormField>
                <FormField labelForId="create-webhook-key-description" labelText="Description" type="textarea">
                  <Textarea
                    id="create-webhook-key-description"
                    value={createWebhookKeyForm.description}
                    onChange={event => handleCreateWebhookKeyFormChange('description', event.target.value)}
                    readOnly={isGeneratingWebhookKey}
                    rows={4}
                  />
                </FormField>
              </Form>
            </>
          )}
        </Modal>
      ) : null}
      {webhookKeyToRevoke ? (
        <Modal
          buttonCancelLabel="Cancel revoke webhook key"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={`Revoke webhook key ${webhookKeyToRevoke.projectKey}`}
          buttonConfirmText={isRevokingWebhookKey ? 'Revoking...' : 'Revoke'}
          confirmationDisabled={isRevokingWebhookKey}
          onCancel={handleRevokeWebhookKeyClose}
          onClose={handleRevokeWebhookKeyClose}
          onConfirm={handleRevokeWebhookKey}
          shown
          status="warning"
          title="Revoke webhook key"
        >
          <>
            {revokeWebhookKeyError ? (
              <div className="app-result app-team-error" role="alert">
                {revokeWebhookKeyError}
              </div>
            ) : null}
            <p className="app-modal-copy">
              Revoke webhook key <strong>{webhookKeyToRevoke.projectKey}</strong>? Jira requests using this key will no
              longer be accepted.
            </p>
          </>
        </Modal>
      ) : null}
      {isCreateModalOpen ? (
        <Modal
          buttonCancelLabel="Cancel create team"
          buttonCancelText={generatedWebhookKey ? undefined : 'Cancel'}
          buttonCloseLabel="Close"
          buttonConfirmLabel={generatedWebhookKey ? 'Close generated webhook key' : 'Save'}
          buttonConfirmText={generatedWebhookKey ? 'OK' : isCreatingTeam ? 'Saving...' : 'Save'}
          confirmationDisabled={generatedWebhookKey ? false : !canSaveCreateTeam}
          onCancel={generatedWebhookKey ? undefined : handleCreateClose}
          onClose={handleCreateClose}
          onConfirm={generatedWebhookKey ? handleCreateTeamResultClose : handleCreateTeam}
          shown
          status={generatedWebhookKey ? 'warning' : undefined}
          title={generatedWebhookKey ? 'Webhook key generated' : 'Create team'}
        >
          {generatedWebhookKey ? (
            <div className="app-webhook-key-result">
              <div className="app-webhook-key-warning" role="alert">
                This key will only be displayed this time and can't be regenerated. Copy it before closing this dialog.
              </div>
              <FormField labelForId="create-team-generated-webhook-key-token" labelText="Generated key" type="textarea">
                <Textarea id="create-team-generated-webhook-key-token" value={generatedWebhookKey.token} readOnly rows={5} />
              </FormField>
              <div className="app-copy-row">
                <Button secondary onClick={handleCopyGeneratedWebhookKey}>
                  Copy generated key
                </Button>
                {copyWebhookKeyStatus ? (
                  <span className="app-copy-status" role="status">
                    {copyWebhookKeyStatus}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {createError ? (
                <div className="app-result app-team-error" role="alert">
                  {createError}
                </div>
              ) : null}
              {installationsError ? (
                <div className="app-result app-team-error" role="alert">
                  {installationsError}
                </div>
              ) : null}
              {agentsError && createForm.generateWebhookKey ? (
                <div className="app-result app-team-error" role="alert">
                  {agentsError}
                </div>
              ) : null}
              <Form
                className="app-create-team-form"
                onSubmit={event => {
                  event.preventDefault();
                  handleCreateTeam();
                }}
              >
                <FormField labelForId="create-team-name" labelText="Name" notion type="input">
                  <Input
                    id="create-team-name"
                    value={createForm.name}
                    onChange={event => handleCreateFormChange('name', event.target.value)}
                    readOnly={isCreatingTeam}
                  />
                </FormField>
                <FormField labelForId="create-team-description" labelText="Description" type="textarea">
                  <Textarea
                    id="create-team-description"
                    value={createForm.description}
                    onChange={event => handleCreateFormChange('description', event.target.value)}
                    readOnly={isCreatingTeam}
                    rows={3}
                  />
                </FormField>
                <FormField labelForId="create-team-installation-id" labelText="Installation ID" type="select">
                  <Select
                    id="create-team-installation-id"
                    value={createForm.installationId}
                    onChange={event => handleCreateFormChange('installationId', event.target.value)}
                    disabled={isCreatingTeam || areInstallationsLoading}
                  >
                    <Select.Item value="">No installation</Select.Item>
                    {unclaimedInstallations.map(installation => (
                      <Select.Item key={installation.installationId} value={String(installation.installationId)}>
                        {`${installation.installationId} - ${installation.accountLogin}`}
                      </Select.Item>
                    ))}
                  </Select>
                </FormField>
                {areInstallationsLoading ? (
                  <div className="app-detail-loading app-detail-loading--compact" role="status">
                    <Spinner center>Loading installations...</Spinner>
                  </div>
                ) : null}
                <div className="app-workflow-panel">
                  <Checkbox
                    id="create-team-generate-webhook-key"
                    checked={createForm.generateWebhookKey}
                    disabled={isCreatingTeam}
                    onChange={event => handleCreateGenerateWebhookKeyChange(event.target.checked)}
                  >
                    Generate webhook key
                  </Checkbox>
                  {createForm.generateWebhookKey ? (
                    <div className="app-workflow-panel-body">
                      {areAgentsLoading ? (
                        <div className="app-detail-loading app-detail-loading--compact" role="status">
                          <Spinner center>Loading agents...</Spinner>
                        </div>
                      ) : null}
                      <FormField labelForId="create-team-webhook-project-key" labelText="Project Key" notion type="input">
                        <Input
                          id="create-team-webhook-project-key"
                          value={createForm.projectKey}
                          onChange={event => handleCreateFormChange('projectKey', event.target.value)}
                          readOnly={isCreatingTeam}
                        />
                      </FormField>
                      <FormField labelForId="create-team-webhook-agent" labelText="Agent" notion type="select">
                        <Select
                          id="create-team-webhook-agent"
                          value={createForm.agentId}
                          onChange={event => handleCreateFormChange('agentId', event.target.value)}
                          disabled={areAgentsLoading || isCreatingTeam}
                        >
                          <Select.Item value="" disabled>
                            Select an agent
                          </Select.Item>
                          {agents.map(agent => (
                            <Select.Item key={agent.agentId} value={agent.agentId}>
                              {agent.name ? `${agent.name} (${agent.agentId})` : agent.agentId}
                            </Select.Item>
                          ))}
                        </Select>
                      </FormField>
                      <FormField labelForId="create-team-webhook-description" labelText="Webhook Key Description" type="textarea">
                        <Textarea
                          id="create-team-webhook-description"
                          value={createForm.webhookKeyDescription}
                          onChange={event => handleCreateFormChange('webhookKeyDescription', event.target.value)}
                          readOnly={isCreatingTeam}
                          rows={3}
                        />
                      </FormField>
                    </div>
                  ) : null}
                </div>
              </Form>
            </>
          )}
        </Modal>
      ) : null}
    </>
  );
}
